import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  IconButton,
  Typography,
  Box,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { ModalProps } from "@_constants/modal";
import { type TabData } from "@utils/dashboard-storage";
import { useTabManagement } from "@/hooks/use-tab-management";
import { useAutoScroll } from "@/hooks/tab-management/use-auto-scroll";
import { useCreateDashboardTabMutation } from "@/store/api/dashboard-api";
import HPButton from "../common/Button";
import { CloseIcon, PlusIconBlue } from "@/assets/images";
import TabRow from "./tab-management/TabRow";
import GroupContainer from "./tab-management/GroupContainer";
import AddGroupPanel from "./tab-management/AddGroupPanel";
import { NewTabRow, NewTabRowSkeleton } from "./tab-management/NewTabRow";

interface TabManagementModalProps extends ModalProps {
  tabs: TabData[];
  onSave: (tabs: TabData[]) => void;
  onAddTab: (name: string) => void;
  onRemoveTab?: (tabId: string) => void;
  onDuplicateTab?: (tabId: string, newName: string) => void;
  dashboardId?: string;
  shouldStartInAddMode?: boolean;
  isDuplicatingTab?: boolean;
}

const TabManagementModal: React.FC<TabManagementModalProps> = ({
  tabs,
  onSave,
  onAddTab,
  onRemoveTab,
  onDuplicateTab,
  dashboardId,
  closeAction,
  shouldStartInAddMode,
  isDuplicatingTab = false,
}) => {
  const [, { isLoading: isCreatingTab }] = useCreateDashboardTabMutation({
    fixedCacheKey: `create-tab-${dashboardId}`,
  });

  const {
    localTabs,
    navItems,
    newTabName,
    setNewTabName,
    newTabError,
    tabErrors,
    dragged,
    dragOverTarget,
    isAddingNewTab,
    hoveredTabId,
    deleteConfirmation,
    groupDeleteConfirmation,
    hoveredNewItem,
    isLoadingTabs,
    canAddGroup,
    saveError,
    dismissSaveError,
    groupCreation,
    newTabInputRef,
    handleUpdateTabName,
    handleTabNameBlur,
    handleAddTab,
    handleSaveNewTab,
    handleCancelNewTab,
    handleDeleteTabClick,
    confirmDeleteTab,
    cancelDeleteTab,
    handleDeleteGroupClick,
    confirmDeleteGroup,
    cancelDeleteGroup,
    handleRenameGroup,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleMouseEnter,
    handleMouseLeave,
    handleNewItemMouseEnter,
    handleNewItemMouseLeave,
    handleDuplicateTab,
  } = useTabManagement({
    tabs,
    onSave,
    onAddTab,
    onRemoveTab,
    onDuplicateTab,
    dashboardId,
    shouldStartInAddMode,
  });

  const { containerRef: scrollContainerRef, handleDragOverAutoScroll, stopAutoScroll } =
    useAutoScroll();
  const handleDragOverWithScroll = (e: React.DragEvent) => {
    handleDragOver(e);
    handleDragOverAutoScroll(e.clientY);
  };
  const handleDragEndWithScrollStop = () => {
    stopAutoScroll();
    handleDragEnd();
  };
  const handleDropWithScrollStop = (e: React.DragEvent) => {
    stopAutoScroll();
    handleDrop(e);
  };

  const dnd = {
    handleDragStart,
    handleDragEnd: handleDragEndWithScrollStop,
    handleDragOver: handleDragOverWithScroll,
    handleDragEnter,
    handleDragLeave,
    handleDrop: handleDropWithScrollStop,
  };
  const canDeleteTab = localTabs.length > 1;
  const canDuplicateTab = localTabs.length < 20;

  return (
    <Dialog
      open={true}
      onClose={closeAction}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 400,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle1" component="div">
          Tabs
        </Typography>
        <IconButton onClick={closeAction} sx={{ p: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        ref={scrollContainerRef}
        dividers
        onDragOver={(e) => handleDragOverAutoScroll(e.clientY)}
        sx={{ p: 2, position: "relative" }}
      >
        <Backdrop
          sx={{
            position: "absolute",
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
          }}
          open={isDuplicatingTab}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress />
          </Box>
        </Backdrop>

        {saveError && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#FDECEA",
              borderRadius: "8px",
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography variant="body2" color="error">
              {saveError.message}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <HPButton variant="text" size="small" onClick={saveError.retry}>
                Retry
              </HPButton>
              <HPButton variant="text" size="small" onClick={dismissSaveError}>
                Dismiss
              </HPButton>
            </Box>
          </Box>
        )}

        {groupCreation.isOpen ? (
          <AddGroupPanel
            localTabs={localTabs}
            name={groupCreation.name}
            nameError={groupCreation.nameError}
            selectedTabIds={groupCreation.selectedTabIds}
            canConfirm={groupCreation.canConfirm}
            previewTabs={groupCreation.previewTabs}
            onNameChange={groupCreation.setName}
            onToggleTab={groupCreation.toggleTab}
            onConfirm={groupCreation.confirm}
            onCancel={groupCreation.close}
          />
        ) : (
          <List sx={{ position: "relative", zIndex: isDuplicatingTab ? 0 : 1 }}>
            {navItems.map((item, navIndex) =>
              item.kind === "tab" ? (
                <TabRow
                  key={item.tab.id}
                  tab={item.tab}
                  isDragging={dragged?.type === "tab" && dragged.tabId === item.tab.id}
                  isDraggedOver={
                    !(dragged?.type === "tab" && dragged.tabId === item.tab.id) &&
                    dragOverTarget?.tabId === item.tab.id &&
                    dragOverTarget.groupName === null
                  }
                  confirmation={
                    deleteConfirmation?.tabId === item.tab.id
                      ? {
                          hasContent: deleteConfirmation.hasContent,
                          affectedGroupName: deleteConfirmation.affectedGroupName,
                        }
                      : null
                  }
                  error={tabErrors[item.tab.id]}
                  isHovered={hoveredTabId === item.tab.id}
                  isDuplicatingTab={isDuplicatingTab}
                  canDelete={canDeleteTab}
                  canDuplicate={canDuplicateTab}
                  onDragStart={(e) => handleDragStart(e, { type: "tab", tabId: item.tab.id })}
                  onDragEnd={handleDragEndWithScrollStop}
                  onDragOver={handleDragOverWithScroll}
                  onDragEnter={(e) =>
                    handleDragEnter(e, { navIndex, tabId: item.tab.id, groupName: null })
                  }
                  onDragLeave={handleDragLeave}
                  onDrop={handleDropWithScrollStop}
                  onNameChange={(value) => handleUpdateTabName(item.tab.id, value)}
                  onNameBlur={(value) => handleTabNameBlur(item.tab.id, value)}
                  onDeleteClick={() => handleDeleteTabClick(item.tab.id)}
                  onDuplicateClick={() => handleDuplicateTab(item.tab.id, item.tab.label)}
                  onConfirmDelete={() => confirmDeleteTab(true)}
                  onCancelDelete={cancelDeleteTab}
                  onMouseEnter={() => handleMouseEnter(item.tab.id)}
                  onMouseLeave={handleMouseLeave}
                />
              ) : (
                <GroupContainer
                  key={item.groupName}
                  groupName={item.groupName}
                  tabs={item.tabs}
                  navIndex={navIndex}
                  dragged={dragged}
                  dragOverTarget={dragOverTarget}
                  isDeleteConfirming={groupDeleteConfirmation?.groupName === item.groupName}
                  tabErrors={tabErrors}
                  hoveredTabId={hoveredTabId}
                  deleteConfirmation={deleteConfirmation}
                  isDuplicatingTab={isDuplicatingTab}
                  canDeleteTab={canDeleteTab}
                  canDuplicateTab={canDuplicateTab}
                  dnd={dnd}
                  onRenameGroup={handleRenameGroup}
                  onDeleteGroupClick={() => handleDeleteGroupClick(item.groupName)}
                  onConfirmDeleteGroup={confirmDeleteGroup}
                  onCancelDeleteGroup={cancelDeleteGroup}
                  onTabNameChange={handleUpdateTabName}
                  onTabNameBlur={handleTabNameBlur}
                  onDeleteTabClick={handleDeleteTabClick}
                  onDuplicateTabClick={handleDuplicateTab}
                  onConfirmDeleteTab={() => confirmDeleteTab(true)}
                  onCancelDeleteTab={cancelDeleteTab}
                  onTabMouseEnter={handleMouseEnter}
                  onTabMouseLeave={handleMouseLeave}
                />
              ),
            )}

            {/* Trailing drop zone: dropping here lands at the end of the dashboard, ungrouped */}
            {dragged && (
              <Box
                onDragOver={handleDragOverWithScroll}
                onDragEnter={(e) =>
                  handleDragEnter(e, { navIndex: navItems.length, tabId: null, groupName: null })
                }
                onDragLeave={handleDragLeave}
                onDrop={handleDropWithScrollStop}
                sx={{
                  // The visual line stays thin; the hoverable hit area is much
                  // taller so the drop target is actually reachable with a mouse.
                  height: "16px",
                  mx: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    borderTop:
                      dragOverTarget?.navIndex === navItems.length && dragOverTarget.tabId === null
                        ? "3px dashed #1976d2"
                        : "3px dashed transparent",
                  }}
                />
              </Box>
            )}

            {isAddingNewTab && !isCreatingTab && !isLoadingTabs && (
              <NewTabRow
                value={newTabName}
                error={newTabError}
                onChange={setNewTabName}
                onSave={handleSaveNewTab}
                onCancel={handleCancelNewTab}
                isHovered={hoveredNewItem}
                onMouseEnter={handleNewItemMouseEnter}
                onMouseLeave={handleNewItemMouseLeave}
                inputRef={newTabInputRef}
              />
            )}
            {isAddingNewTab && (isLoadingTabs || isCreatingTab) && <NewTabRowSkeleton />}
          </List>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          flexDirection: "row-reverse",
          gap: "8px",
        }}
        className="flex flex-start "
      >
        <Typography variant="subtitle2" className="text-[#A6A6A6]">
          Max 20 tabs
        </Typography>

        {!groupCreation.isOpen && (
          <>
            <HPButton
              ariaLabel="Manage Tabs"
              variant="outlined"
              size="small"
              onClick={handleAddTab}
              startIcon={<PlusIconBlue />}
              disabled={localTabs.length >= 20 || isAddingNewTab}
            >
              Add Tab
            </HPButton>
            <HPButton
              ariaLabel="Add Group"
              variant="outlined"
              size="small"
              onClick={groupCreation.open}
              startIcon={<PlusIconBlue />}
              disabled={!canAddGroup}
            >
              Add Group
            </HPButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TabManagementModal;
